import {ScrollView, Text, TouchableOpacity, View, Pressable, StyleSheet} from "react-native";
import React, {useRef, useState} from "react";
import ScrollFilterItem from "./ScrollFilterItem";
import MaterialCommunityIcons from "@expo/vector-icons/MaterialCommunityIcons";
import CampTypes from "../../../../data/CampTypes";
 

const ScrollFilter = ({
    isSessionView,
    setIsSessionView,
    setFilterModalVisible,
    filterCount,
    selectedCategories,
    setSelectedCategories,
}) => {
    const scrollViewRef = useRef(null);
    const [currentScrollPosition, setCurrentScrollPosition] = useState(0);
    
    const ICON_WIDTH = 100; // Width of each filter item
    const ICONS_PER_SCROLL = 5; // Number of icons to scroll at once
    const SCROLL_DISTANCE = ICON_WIDTH * ICONS_PER_SCROLL; // 500px

    const scrollToLeft = () => {
        if (scrollViewRef.current) {
            const newPosition = Math.max(0, currentScrollPosition - SCROLL_DISTANCE);
            scrollViewRef.current.scrollTo({x: newPosition, animated: true});
            setCurrentScrollPosition(newPosition);
        }
    };

    const scrollToRight = () => {
        if (scrollViewRef.current) {
            const newPosition = currentScrollPosition + SCROLL_DISTANCE;
            scrollViewRef.current.scrollTo({x: newPosition, animated: true});
            setCurrentScrollPosition(newPosition);
        }
    };

    const showFilterModal = () => {
        setFilterModalVisible(true);
    };

    return (
        <View style={styles.container}>
            <TouchableOpacity style={styles.leftButton} onPress={scrollToLeft}>
                <MaterialCommunityIcons name={'chevron-left'} size={24}/>
            </TouchableOpacity>
            <ScrollView 
                horizontal={true} 
                style={styles.scrollView} 
                ref={scrollViewRef} 
                showsHorizontalScrollIndicator={false}
                onScroll={(event) => {
                    setCurrentScrollPosition(event.nativeEvent.contentOffset.x);
                }}
                scrollEventThrottle={16}
            >
                {CampTypes.map((item) => {
                    const isAll = item.text === 'All';
                    const isActive = isAll ? (selectedCategories?.length === 0) : (selectedCategories?.includes(item.text));
                    const handlePress = () => {
                        if (isAll) {
                            setSelectedCategories([]);
                        } else {
                            // Multi-select behavior matching modal
                            const newCategories = selectedCategories?.includes(item.text)
                                ? selectedCategories.filter(c => c !== item.text)
                                : [...(selectedCategories || []), item.text];
                            setSelectedCategories(newCategories);
                        }
                    };
                    return (
                        <ScrollFilterItem 
                            key={item.iconName}
                            iconName={item.iconName}
                            text={item.text}
                            isActive={!!isActive}
                            onPress={handlePress}
                        />
                    );
                })}
            </ScrollView>
            <Pressable style={styles.rightButton} onPress={scrollToRight}>
                <MaterialCommunityIcons name={'chevron-right'} size={24}/>
            </Pressable>

            <Pressable 
                style={({ pressed }) => ([
                    styles.filterButton,
                    filterCount > 0 && styles.filterButtonActive,
                    pressed && { opacity: 0.9 }
                ])} 
                onPress={showFilterModal}
            >
                <View style={[
                    styles.filterIconWrap,
                    filterCount > 0 && styles.filterIconWrapActive
                ]}>
                    <MaterialCommunityIcons 
                        name={'tune-variant'} 
                        size={18} 
                        color={filterCount > 0 ? '#FFFFFF' : 'black'}
                    />
                </View>
                <Text style={[
                    styles.filterText,
                    filterCount > 0 && styles.filterTextActive
                ]}> {isSessionView ? 'Session Filters' : 'Camp Filters'} </Text>
                {filterCount > 0 && (
                    <View style={styles.filterCount}>
                        <Text style={styles.filterCountText}>{filterCount}</Text>
                    </View>
                )}
            </Pressable>

            <Pressable 
                style={({ pressed }) => ([
                    styles.sessionToggle,
                    pressed && { opacity: 0.9 }
                ])}
                onPress={() => setIsSessionView(!isSessionView)}
            >
                <View style={[
                    styles.toggleIconWrap,
                    isSessionView && styles.toggleIconWrapActive
                ]}>
                    <MaterialCommunityIcons 
                        name={isSessionView ? 'campfire' : 'clipboard-list-outline'} 
                        size={18} 
                        color={isSessionView ? '#FFFFFF' : '#374151'}
                    />
                </View>
                <Text style={[
                    styles.sessionToggleText,
                    isSessionView && styles.sessionToggleTextActive
                ]}>
                    {isSessionView ? 'Show Camps   ' : 'Show Sessions'}
                </Text>
            </Pressable>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 50,
        marginBottom: 30,
    },
    leftButton: {
        borderRadius: 999,
        marginRight: 20,
        borderWidth: 1,
        borderColor: '#E5E7EB',
        backgroundColor: '#FFFFFF',
        shadowColor: '#000',
        shadowOpacity: 0.06,
        shadowRadius: 8,
        shadowOffset: { width: 0, height: 3 },
        elevation: 2,
    },
    scrollView: {
        flexDirection: 'row',
        flexGrow: 1,
    },
    rightButton: {
        borderRadius: 999,
        marginLeft: 10,
        borderColor: '#E5E7EB',
        borderWidth: 1,
        marginRight: 30,
        backgroundColor: '#FFFFFF',
        shadowColor: '#000',
        shadowOpacity: 0.06,
        shadowRadius: 8,
        shadowOffset: { width: 0, height: 3 },
        elevation: 2,
    },
    filterButton: {
        borderRadius: 999,
        flexDirection: 'row',
        paddingHorizontal: 14,
        paddingVertical: 8,
        alignItems: 'center',
        gap: 8,
        borderColor: '#E5E7EB',
        borderWidth: 1,
        marginRight: 10,
        backgroundColor: '#FFFFF',
        shadowColor: '#000',
        shadowOpacity: 0.06,
        shadowRadius: 8,
        shadowOffset: { width: 0, height: 3 },
        elevation: 2,
    },
    filterButtonActive: {
        backgroundColor: '#FFFFFF',
    },
    filterIconWrap: {
        width: 28,
        height: 28,
        borderRadius: 14,
        backgroundColor: '#F3F4F6',
        alignItems: 'center',
        justifyContent: 'center',
    },
    filterIconWrapActive: {
        backgroundColor: 'black',
    },
    filterText: {
        fontSize: 14,
        fontWeight: '600',
        color: '#374151',
    },
    filterTextActive: {
        color: 'black',
    },
    filterCount: {
        position: 'absolute',
        top: -6,
        right: -6,
        borderRadius: 12,
        minWidth: 20,
        height: 20,
        paddingHorizontal: 6,
        backgroundColor: '#3C8248',
        zIndex: 10,
        borderColor: 'white',
        borderWidth: 2,
        alignItems: 'center',
        justifyContent: 'center',
    },
    filterCountText: {
        color: 'white',
        fontSize: 10,
        fontWeight: '700',
    },
    sessionToggle: {
        borderRadius: 999,
        borderWidth: 1,
        flexDirection: 'row',
        paddingHorizontal: 14,
        paddingVertical: 8,
        alignItems: 'center',
        gap: 8,
        borderColor: '#E5E7EB',
        backgroundColor: '#FFFFFF',
        shadowColor: '#000',
        shadowOpacity: 0.06,
        shadowRadius: 8,
        shadowOffset: { width: 0, height: 3 },
        elevation: 2,
    },
    toggleIconWrap: {
        width: 28,
        height: 28,
        borderRadius: 14,
        backgroundColor: '#F3F4F6',
        alignItems: 'center',
        justifyContent: 'center',
    },
    toggleIconWrapActive: {
        backgroundColor: '#111827',
    },
    sessionToggleText: {
        fontSize: 14,
        fontWeight: '600',
        color: '#374151',
    },
    sessionToggleTextActive: {
        color: '#111827',
    },
});

export default ScrollFilter;